/**
 * LegalPageLayout
 *
 * Thin wrapper that renders the long-form legal policies (Privacy, Terms,
 * Cookies) inside the shared marketing shell (PublicPageLayout) using its
 * readable "document" body — a bordered card. The branded hero, top bar and
 * footer (with links to every public page) come from PublicPageLayout, so the
 * legal pages match the rest of the public site while keeping their content
 * easy to read.
 *
 * Kept as a separate component so the individual policy pages don't need to
 * change; they still render `<LegalPageLayout title=... lastUpdated=...>`.
 */

import { ReactNode } from 'react';
import PublicPageLayout from './PublicPageLayout';

export type LegalPageKey = 'privacy' | 'terms' | 'cookies';

export interface LegalPageLayoutProps {
  /** Page title shown in the hero. */
  title: string;
  /** Human-readable "last updated" date. */
  lastUpdated: string;
  /**
   * Retained for API compatibility with the policy pages. Cross-navigation now
   * lives in the shared footer, so this is no longer used for rendering.
   */
  current?: LegalPageKey;
  /** Optional icon shown in the hero chip. */
  icon?: ReactNode;
  children: ReactNode;
}

export default function LegalPageLayout({
  title,
  lastUpdated,
  icon,
  children,
}: LegalPageLayoutProps) {
  return (
    <PublicPageLayout
      eyebrow="CCMS Apps · Legal"
      title={title}
      subtitle={`Last updated: ${lastUpdated}`}
      icon={icon}
      variant="document"
    >
      {children}
    </PublicPageLayout>
  );
}
